import { EditOutlined, ScheduleOutlined } from '@mui/icons-material';
import { Box, Button, Chip, Typography } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPercent } from '../../../library/dashboardDisplay';
import {
    ITherapistRevenueShare,
    hasScheduledChange,
} from '../../../library/models/therapistRevenueShare';
import ResourcesIds from '../../../resources/ResourcesIds';
import {
    readOnlyValueStyles,
    scheduledChipStyles,
    triggerBoxStyles,
    triggerIconStyles,
    triggerStyles,
} from './therapistShare.styles';

interface TherapistShareTriggerProps {
    therapistName: string;
    /** Every scope this therapist has a rate in, as the API sends them; empty for none. */
    shares: ITherapistRevenueShare[];
    /** False for a member without live access: their rate is shown, never edited. */
    isEditable: boolean;
    onOpen: (anchor: HTMLElement) => void;
}

/**
 * What a therapist keeps, in one table cell, and the way to change it.
 *
 * <b>The cell shows the rate in force — not the one last typed.</b> A rate takes force on the
 * first of the month after it is set, so for most of any month those differ, and the in-force one
 * is the figure every amount on the clinic dashboard was computed at. Showing it is what lets an
 * administrator read the two against each other, which is the thing they actually do with them.
 * A queued change is flagged rather than substituted, and the editor holds its date.
 *
 * <b>One value and at most two chips, and nothing else</b> — the default rate, how many
 * disciplines are paid differently, and whether a change is queued. This cell used to be a text
 * input, a tick, a clear button and a line of date text all at once, and every layout problem it had came from
 * that rather than from any of them. The editor moved to a popover anchored here; what is left is
 * a value you can click.
 */
export const TherapistShareTrigger = (props: TherapistShareTriggerProps): JSX.Element => {
    const { t: translate } = useTranslation();

    const percent: number | null =
        props.shares.find(share => share.catalogTypeId === null)?.percent ?? null;

    /** Discipline overrides with a rate in force. Named in the editor, counted here. */
    const overrides: number = props.shares
        .filter(share => share.catalogTypeId !== null && share.percent !== null).length;

    const overridesChip: JSX.Element | null = overrides > 0 ? (
        <Chip
            size="small"
            variant="outlined"
            sx={scheduledChipStyles}
            label={translate(ResourcesIds.REVENUE_SHARE_OVERRIDES_CHIP, { count: overrides })}
        />
    ) : null;

    const label: string = percent === null
        ? translate(ResourcesIds.REVENUE_SHARE_NONE)
        : translate(ResourcesIds.REVENUE_SHARE_PERCENT_VALUE, {
            percent: formatPercent(percent),
        });

    if (!props.isEditable) {
        return (
            <Box sx={triggerBoxStyles}>
                <Typography variant="body2" sx={readOnlyValueStyles}>{label}</Typography>
                {overridesChip}
            </Box>
        );
    }

    return (
        <Box sx={triggerBoxStyles}>
            <Button
                size="small"
                sx={triggerStyles}
                onClick={event => props.onOpen(event.currentTarget)}
                aria-label={translate(ResourcesIds.REVENUE_SHARE_EDIT_LABEL, {
                    name: props.therapistName,
                })}
            >
                {label}
                <EditOutlined sx={triggerIconStyles} />
            </Button>

            {overridesChip}

            {/* Keyed on the date rather than the percent: a queued *clearing* carries a null
                percent and a real date, and that is the change most worth flagging. Any scope
                counts -- a discipline's change is as much a change to their pay. */}
            {props.shares.some(hasScheduledChange) && (
                <Chip
                    size="small"
                    variant="outlined"
                    sx={scheduledChipStyles}
                    icon={<ScheduleOutlined />}
                    label={translate(ResourcesIds.REVENUE_SHARE_SCHEDULED)}
                />
            )}
        </Box>
    );
};
